import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

/**
 * Ancho del modal. No es decoración: un formulario a dos columnas dentro de
 * `estrecho` deja los campos de 200 px y obliga a leer en zigzag. El de un
 * cliente —logotipo, país, sector, contacto— pide `ancho`.
 */
type Ancho = 'estrecho' | 'medio' | 'ancho';

const MAXIMOS: Record<Ancho, string> = {
  estrecho: 'max-w-lg', // confirmaciones, un motivo, una pregunta
  medio: 'max-w-2xl', // formulario de una columna
  ancho: 'max-w-3xl', // formulario a dos columnas
};

type Props = {
  titulo: string;
  ancho?: Ancho;
  abierto: boolean;
  /** Si hay algo escrito, cerrar pide confirmación. Regla fija del stack. */
  hayCambios?: boolean;
  alCerrar: () => void;
  pie?: ReactNode;
  children: ReactNode;
};

/**
 * Modal en escritorio, hoja a pantalla completa en móvil.
 *
 * Usa `<dialog>` nativo: trae el atrapado del foco, el cierre con Escape y el
 * fondo inerte sin que haya que implementarlos. Una versión con `<div>` los
 * necesita todos, y lo que se olvida siempre es que se pueda tabular fuera.
 */
export function Modal({
  titulo,
  ancho = 'estrecho',
  abierto,
  hayCambios,
  alCerrar,
  pie,
  children,
}: Props) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (abierto && !d.open) d.showModal();
    if (!abierto && d.open) d.close();
  }, [abierto]);

  function intentarCerrar() {
    if (hayCambios && !confirm('Hay cambios sin guardar. ¿Descartarlos?')) return;
    alCerrar();
  }

  return (
    <dialog
      ref={ref}
      onCancel={(e) => {
        // Escape pasa por acá: se intercepta para poder preguntar.
        e.preventDefault();
        intentarCerrar();
      }}
      onClick={(e) => {
        // Clic en el fondo, no en el contenido.
        if (e.target === ref.current) intentarCerrar();
      }}
      className={`m-auto w-full ${MAXIMOS[ancho]} rounded-2xl border border-marino-100 p-0
        backdrop:bg-marino-900/40 max-sm:h-full max-sm:max-h-full max-sm:max-w-full max-sm:rounded-none`}
    >
      <div className="flex max-h-[85vh] flex-col max-sm:h-full max-sm:max-h-full">
        <header className="flex items-center gap-4 border-b border-marino-100 px-6 py-4">
          <h2 className="text-lg font-bold text-marino-800">{titulo}</h2>
          <div className="flex-1" />
          <button
            type="button"
            onClick={intentarCerrar}
            aria-label="Cerrar"
            className="rounded-full p-2 text-marino-400 transition hover:bg-marino-50 hover:text-marino-700"
          >
            ✕
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>

        {pie && (
          <footer className="flex justify-end gap-3 border-t border-marino-100 px-6 py-4">
            {pie}
          </footer>
        )}
      </div>
    </dialog>
  );
}
